import { Waveform, ArrowLeft, Phone, FileText } from "@phosphor-icons/react";
import { useNavigate, Link } from "react-router-dom";

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      <header className="relative z-10 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-5">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-white/60 hover:text-white transition-colors" data-testid="terms-back-btn">
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center gap-3">
              <Waveform size={28} weight="bold" style={{ color: "#F59E0B" }} />
              <span className="text-xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Daremusics</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-8">
          <FileText size={32} weight="bold" style={{ color: "#F59E0B" }} />
          <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Terms & Conditions
          </h1>
        </div>

        <div className="bg-[#141417] border border-white/5 rounded-2xl p-6 md:p-8 space-y-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <p className="text-sm" style={{ color: "#A1A1AA" }}>Last updated: April 2026</p>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">1. Acceptance of Terms</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              By accessing and using Daremusics, you agree to be bound by these Terms & Conditions. If you do not agree with any part of these terms, you may not use our services.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">2. Service Description</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              Daremusics is an AI-powered platform that generates Telugu song lyrics and music based on user-provided concepts. We use advanced AI technology for lyrics generation and music production. The quality and output of AI-generated content may vary.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">3. Subscription Plans</h3>
            <ul className="space-y-2" style={{ color: "#A1A1AA" }}>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0" />
                <strong className="text-white">Starter Plan:</strong> &#8377;149/week - 1 song generation credit
              </li>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0" />
                <strong className="text-white">Mid Plan:</strong> &#8377;349/week - 6 song generation credits
              </li>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0" />
                <strong className="text-white">Pro Plan:</strong> &#8377;499/week - 16 song generation credits
              </li>
            </ul>
            <p className="text-base leading-relaxed mt-3" style={{ color: "#A1A1AA" }}>
              All subscriptions are valid for 7 days from the date of purchase. Unused song credits expire after the subscription period and cannot be carried forward or transferred.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">4. Account Responsibility</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account. Daremusics is not liable for any loss arising from unauthorized access to your account.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">5. Content Ownership</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              Songs and lyrics generated through Daremusics are created using AI technology. While you have the right to use the generated content for personal and commercial purposes, Daremusics does not guarantee exclusivity of the content. Similar inputs may produce similar outputs for different users.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">6. Prohibited Uses</h3>
            <ul className="space-y-2" style={{ color: "#A1A1AA" }}>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#EF4444] shrink-0" />
                Using the service to generate offensive, harmful, or illegal content
              </li>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#EF4444] shrink-0" />
                Attempting to reverse-engineer or exploit the platform
              </li>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#EF4444] shrink-0" />
                Sharing account credentials with unauthorized users
              </li>
              <li className="flex items-start gap-2 text-base leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#EF4444] shrink-0" />
                Using automated tools to generate bulk content
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">7. Payment Terms</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              All payments are processed securely through Razorpay. Prices are in Indian Rupees (INR). By making a payment, you agree to Razorpay's terms of service. For refund details, please refer to our <Link to="/refund-policy" className="text-[#F59E0B] hover:text-white transition-colors">Refund Policy</Link>.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">8. Limitation of Liability</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              Daremusics is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from the use of our services, including but not limited to loss of revenue or data.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">9. Modifications</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              Daremusics reserves the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h3 className="text-lg text-white font-medium mb-3">10. Governing Law</h3>
            <p className="text-base leading-relaxed" style={{ color: "#A1A1AA" }}>
              These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts in India.
            </p>
          </section>

          <div className="border-t border-white/10 pt-6">
            <div className="flex items-center gap-2 mb-3">
              <Phone size={20} weight="bold" style={{ color: "#F59E0B" }} />
              <span className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: "#F59E0B" }}>Contact Us</span>
            </div>
            <p className="text-sm mb-2" style={{ color: "#A1A1AA" }}>Daremusics</p>
            <div className="flex gap-6">
              <a href="tel:9493926054" className="text-white hover:text-[#F59E0B] transition-colors">9493926054</a>
              <a href="tel:9493924588" className="text-white hover:text-[#F59E0B] transition-colors">9493924588</a>
            </div>
            <div className="mt-3">
              <Link to="/privacy-policy" className="text-sm hover:text-white transition-colors" style={{ color: "#F59E0B" }}>Privacy Policy</Link>
              <span className="text-white/20 mx-3">|</span>
              <Link to="/refund-policy" className="text-sm hover:text-white transition-colors" style={{ color: "#F59E0B" }}>Refund Policy</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
